import React, { useState } from 'react';
import Modal from './Modal';
import BlogPanel from './BlogPanel';

/**
 * In-app help: how envelope budgeting works here, and what each tab does.
 * Static content — one expandable section per topic so the modal stays
 * scannable instead of being a wall of text.
 */
const SECTIONS = [
  {
    id: 'method',
    title: 'The big idea: every dollar gets a job',
    body: (
      <>
        <p>
          Money that arrives (income, live cash/savings openings, and liquid cash) lands in one pot —{' '}
          <strong>Up for Grabs</strong> — and your job is to grab it and assign it to categories
          (envelopes) until Up for Grabs hits <strong>$0.00</strong>. Live starting balances on
          checking and savings count here the same way a spreadsheet tags them Assign. A leftover
          sitting on a loan or card register does not.
        </p>
        <p>
          When you spend, the transaction pulls from its category's envelope, so{' '}
          <strong>Available = Grabbed − Spent</strong> always tells you what's truly left — not what
          your bank balance implies.
        </p>
      </>
    ),
  },
  {
    id: 'budget',
    title: 'Budget tab — grab dollars into envelopes',
    body: (
      <>
        <p>
          The hero number is <strong>Up for Grabs</strong>: income deposits, liquid cash, and
          live checking/savings openings, minus everything you've grabbed. A leftover on a
          loan or card register is not part of that pot. Assign amounts in the{' '}
          <strong>Grabbed</strong> column (or hit{' '}
          <strong>Grab all</strong> to fill every envelope to its Planned amount).{' '}
          <strong>Available</strong> is Grabbed minus Spent. The Emergency Fund envelope also
          includes Tracking invested-liquid accounts (a brokerage emergency fund), not the bank
          or cash accounts already sitting in that envelope.
        </p>
        <p>
          <strong>Planned</strong> is the live Strategy amount for that envelope — extra payoff
          and surplus included, without waiting on Save all. The <strong>=</strong> button fills
          Grabbed to match it.
        </p>
        <p>
          Each envelope carries a pill saying how the strategy treats it —{' '}
          <strong>Fixed</strong>, <strong>Variable</strong>, <strong>Savings</strong> or{' '}
          <strong>Debt</strong>. It's a label, not a switch: the class is whatever Setup says
          drives that category. A spending goal makes it Variable, a savings plan makes it
          Savings, a debt plan makes it Debt, and an envelope nothing points at is Fixed.
        </p>
        <p>
          That's also the order cuts land in: variable spending absorbs most of a shortfall,
          then debt extras, then savings. Fixed costs are never cut. To change how an envelope
          behaves, change what's linked to it in <strong>Setup</strong> — there's no way for the
          label and the math to disagree.
        </p>
      </>
    ),
  },
  {
    id: 'ledger',
    title: 'Ledger tab — record what actually happened',
    body: (
      <>
        <p>
          Enter every deposit, debit, and transfer here. Each transaction names an account (where
          the money moved) and, for spending, a category (which envelope it pulls from).
        </p>
        <p>
          The ledger is the source of truth: live account balances and each envelope's Spent amount
          are computed from these rows, so the more faithfully you record, the more the app reflects
          reality.
        </p>
      </>
    ),
  },
  {
    id: 'accounts',
    title: 'Accounts tab — balances and net worth',
    body: (
      <>
        <p>
          All your accounts grouped by type: Budget (cash), Credit Cards, Loans, Investments, and
          Assets — rolled up into a live <strong>Net Worth</strong>.
        </p>
        <p>
          Budget accounts marked <strong>Live</strong> compute their balance from the ledger
          automatically and can't be typed over — that keeps the balance honest. Accounts set to{' '}
          <strong>Manual</strong> (loans, investments, home value) you update from statements.
          Click the Live/Manual pill to switch an account between modes, and click any account's
          name to rename it.
        </p>
        <p>
          The <strong>⚡ Liquid</strong> toggle marks an account as emergency savings — it then
          counts toward the Accounts-tab Emergency Fund total. The Budget envelope's Available
          only adds Tracking-type invested-liquid accounts (a brokerage emergency fund), not
          bank or cash accounts that already hold money you grabbed into the envelope.
        </p>
      </>
    ),
  },
  {
    id: 'strategy',
    title: 'Strategy tab — goals, cuts, and the monthly plan',
    body: (
      <>
        <p>
          Income comes from Setup. Variable spending goals can be set here; savings and debt
          monthly amounts are calculated from the plans you detail in Setup. The strategy engine
          turns them into a <strong>Planned</strong> amount per category, which feeds the Budget tab.
        </p>
        <p>
          If your goals add up to more than net income, <strong>cuts cascade</strong>: variable
          spending absorbs most of the shortfall first, then debt extras, then savings — fixed costs
          are never cut. The Cut column shows exactly what got trimmed and by how much.
        </p>
        <p>
          Hit <strong>Save all</strong> to write the plan into your envelopes.
        </p>
      </>
    ),
  },
  {
    id: 'trends',
    title: 'Trends tab — what you actually spend',
    body: (
      <>
        <p>
          Trends looks across every month you've budgeted, not just this one. It
          charts spending by category group and lets you plot up to six
          categories against each other — pick one on its own and its{' '}
          <strong>Planned</strong> line comes along for comparison.
        </p>
        <p>
          Underneath, <strong>Suggested adjustments</strong> compares what a
          category actually costs you against what you plan for it, using
          completed months only — the month in progress is left out so a
          half-finished month can't drag the average down. Accepting a suggestion
          writes the new amount onto the linked spending goal in Setup; run{' '}
          <strong>Save all</strong> in Strategy to push it through to your
          envelopes.
        </p>
        <p>
          None of this is recorded separately — it's computed from your months
          and ledger, so it fills in for months you budgeted before the tab
          existed, and fixing an old entry corrects the charts straight away.
        </p>
      </>
    ),
  },
  {
    id: 'blog',
    title: 'Blog — writing for other members',
    body: (
      <>
        <p>
          The <strong>Blog</strong> tab at the top of this window is a shared
          space for members to write about budgeting. Anyone can submit a post,
          but nothing appears until a <strong>moderator has read it</strong> —
          there is no way to publish straight to the blog.
        </p>
        <p>
          Posts are screened automatically first. Slurs and hard profanity are{' '}
          <strong>refused outright</strong>; insults, self-harm language and
          financial pitches ("guaranteed returns", "DM me") are flagged for a
          closer read. Posting is capped at three posts awaiting review and five
          a day, and any published post can be reported by a reader — enough
          reports and it comes down pending re-review.
        </p>
        <p>
          Your email is never published; posts carry the display name you choose.
          Don't post account numbers or anything you'd rather other members
          didn't know about you.
        </p>
      </>
    ),
  },
  {
    id: 'setup',
    title: 'Setup tab — the household model',
    body: (
      <>
        <p>
          Setup is where the structure lives: household members and their incomes, savings plans
          (401k, IRA, brokerage) with the balances, rates, and targets that compute each monthly
          Goal, debt plans with payoff dates, and variable spending goals.
        </p>
        <p>
          Linking is what makes an envelope cuttable. A spending goal linked to a category makes
          that category Variable; a savings or debt plan linked to one makes it Savings or Debt.
          Anything left unlinked is a fixed cost that cuts never touch — which is the safe
          default, not an error.
        </p>
        <p>
          Point only one thing at a category. If a goal and a plan both claim the same envelope,
          the Strategy tab flags it, because only one of them can decide its Planned amount.
        </p>
      </>
    ),
  },
  {
    id: 'privacy',
    title: 'Your data & privacy',
    body: (
      <>
        <p>
          Everything here is manual entry, by design. <strong>No logins. No syncing. No
          exceptions.</strong> Your bank credentials never touch Trigger Budget, because we never
          ask for them. We wouldn't link our own accounts to an app — we're not about to ask you to.
        </p>
        <p>
          You can enable two-factor authentication any time from the <strong>Security</strong>{' '}
          button in the header.
        </p>
      </>
    ),
  },
];

export default function HelpModal({ onClose }) {
  const [open, setOpen] = useState('method');
  const [tab,  setTab]  = useState('guide');

  return (
    <Modal title={tab === 'guide' ? 'How Trigger Budget works' : 'Blog'} onClose={onClose} wide>
      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        <button className={`btn btn-sm${tab === 'guide' ? ' btn-primary' : ''}`}
          onClick={() => setTab('guide')}>Guide</button>
        <button className={`btn btn-sm${tab === 'blog' ? ' btn-primary' : ''}`}
          onClick={() => setTab('blog')}>Blog</button>
      </div>

      {tab === 'blog' ? <BlogPanel /> : <Guide open={open} setOpen={setOpen} />}

      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <div style={{ flex: 1, fontSize: 11, color: 'var(--text3)', alignSelf: 'center' }}>
          Stuck on something? Use <strong>Feedback</strong> in the header — we read everything.
        </div>
        <button className="btn btn-primary" onClick={onClose}>Got it</button>
      </div>
    </Modal>
  );
}

function Guide({ open, setOpen }) {
  return (
    <>
      <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 14, lineHeight: 1.5 }}>
        Run Wild. Spend Smart. — a quick tour of the app, one section at a time.
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 440, overflowY: 'auto' }}>
        {SECTIONS.map(s => {
          const isOpen = open === s.id;
          return (
            <div key={s.id}
              style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
              <button
                onClick={() => setOpen(isOpen ? null : s.id)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  width: '100%', textAlign: 'left', padding: '10px 12px',
                  background: isOpen ? 'var(--surface2)' : 'transparent',
                  border: 'none', color: 'var(--text)', fontSize: 13, fontWeight: 600,
                  cursor: 'pointer',
                }}>
                {s.title}
                <span style={{ color: 'var(--text3)', fontSize: 11, marginLeft: 8 }}>
                  {isOpen ? '▲' : '▼'}
                </span>
              </button>
              {isOpen && (
                <div className="help-body"
                  style={{ padding: '2px 12px 10px', fontSize: 12.5, color: 'var(--text2)', lineHeight: 1.6 }}>
                  {s.body}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
